import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { makeRequest2 } from "../../../utils/makeRequest";
import { getItem } from "../../../utils/localStorage";
import { toastify } from "../../../utils/toast";
import {
    addCommunityGuidelinesURL,
    getCommunityGuidelinesURL
} from "../../../utils/urls";

const CommunityGuidelines = () => {
    const [guidelines, setGuidelines] = useState("");
    const [loading, setLoading] = useState(true);
    const [contentType, setContentType] = useState('text');
    const [pdfFile, setPdfFile] = useState(null);
    const [existingPdfUrl, setExistingPdfUrl] = useState(null);
    const [existingPdfName, setExistingPdfName] = useState("");

    // Load existing guidelines
    useEffect(() => {
        const fetchGuidelines = async () => {
            try {
                console.log(111111);
                const facilityId = await getItem("selectedFacilityId");
                const response = await makeRequest2(
                    `${getCommunityGuidelinesURL}/${facilityId}`,
                    "GET"
                );

                console.log("Guidelines response:", response);

                if (response.success) {
                    const data = response.data.guidelines;
                    setGuidelines(data.content || "");
                    setContentType(data.contentType || 'text');

                    if (data.contentType === 'pdf' && data.pdfUrl) {
                        setExistingPdfUrl(data.pdfUrl);
                        setExistingPdfName(data.pdfFileName || "Existing PDF");
                    }
                } else {
                    setGuidelines("");
                    setContentType('text');
                }
            } catch (error) {
                console.error(error);
                toastify("Failed to load community guidelines", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchGuidelines();
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                toastify("Please select a PDF file", "error");
                e.target.value = ''; // Reset file input
                return;
            }
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                toastify("File size must be less than 10MB", "error");
                e.target.value = '';
                return;
            }
            setPdfFile(file);
            setExistingPdfUrl(null); // Clear existing PDF preview when uploading new one
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (contentType === 'text' && !guidelines.trim()) {
            toastify("Please enter community guidelines content", "error");
            return;
        }

        if (contentType === 'pdf' && !pdfFile && !existingPdfUrl) {
            toastify("Please upload a PDF file", "error");
            return;
        }

        try {
            const facilityId = await getItem("selectedFacilityId");
            const formData = new FormData();

            formData.append('content', guidelines);
            formData.append('contentType', contentType);

            if (contentType === 'pdf' && pdfFile) {
                formData.append('file', pdfFile);
            }

            const response = await makeRequest2(
                `${addCommunityGuidelinesURL}/${facilityId}`,
                "POST",
                formData,
                true // Set to true for FormData requests
            );

            console.log("Submit Response:", response);

            if (response.success) {
                toastify("Community Guidelines saved successfully", "success");

                // Reset file input after successful upload
                if (contentType === 'pdf' && pdfFile) {
                    setPdfFile(null);
                    setExistingPdfUrl(response.data.guidelines.pdfUrl);
                    setExistingPdfName(response.data.guidelines.pdfFileName);
                    // Clear file input
                    document.getElementById('pdf-upload').value = '';
                }
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            console.error(err);
            toastify("Error saving guidelines", "error");
        }
    };

    const handleContentTypeChange = (type) => {
        setContentType(type);
        if (type === 'pdf') {
            // Clear text content when switching to PDF
            setGuidelines("");
        }
    };

    const removePdfFile = () => {
        setPdfFile(null);
        setExistingPdfUrl(null);
        setExistingPdfName("");
        document.getElementById('pdf-upload').value = '';
    };

    return (
        <div className="container my-5" style={{ maxWidth: "90%" }}>
            <div className="card shadow-sm border-0">
                <div className="card-body">
                    <h4 className="mb-4">Community Guidelines</h4>

                    {loading ? (
                        <p>Loading...</p>
                    ) : (
                        <>
                            {/* Content Type Selection */}
                            <div className="mb-4">
                                <label className="form-label fw-bold">Select Content Type:</label>
                                <div className="d-flex gap-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="contentType"
                                            id="textType"
                                            checked={contentType === 'text'}
                                            onChange={() => handleContentTypeChange('text')}
                                        />
                                        <label className="form-check-label" htmlFor="textType">
                                            Text Content
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="contentType"
                                            id="pdfType"
                                            checked={contentType === 'pdf'}
                                            onChange={() => handleContentTypeChange('pdf')}
                                        />
                                        <label className="form-check-label" htmlFor="pdfType">
                                            PDF Document
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Text Content Editor */}
                            {contentType === 'text' && (
                                <>
                                    <ReactQuill
                                        theme="snow"
                                        value={guidelines}
                                        onChange={setGuidelines}
                                        placeholder="Write your community guidelines here..."
                                        style={{ height: "300px", marginBottom: "60px" }}
                                    />
                                    <div className="alert alert-info mt-3">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Enter your guidelines using the rich text editor above. This supports formatting, lists, and other styling options.
                                    </div>
                                </>
                            )}

                            {/* PDF Upload Section */}
                            {contentType === 'pdf' && (
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Upload PDF Document:</label>
                                    <input
                                        type="file"
                                        id="pdf-upload"
                                        className="form-control"
                                        accept=".pdf,application/pdf"
                                        onChange={handleFileChange}
                                    />
                                    <div className="form-text">
                                        Upload a PDF file (max 10MB). This is ideal for scanned documents or non-selectable text.
                                    </div>

                                    {/* PDF Preview/Info */}
                                    {(pdfFile || existingPdfUrl) && (
                                        <div className="mt-3 p-3 border rounded bg-light">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <i className="bi bi-file-earmark-pdf text-danger me-2"></i>
                                                    <strong>
                                                        {pdfFile ? pdfFile.name : existingPdfName}
                                                    </strong>
                                                    <div className="text-muted small">
                                                        {pdfFile ? `Size: ${(pdfFile.size / 1024 / 1024).toFixed(2)} MB` : 'Previously uploaded'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={removePdfFile}
                                                    >
                                                        <i className="bi bi-trash"></i> Remove
                                                    </button>
                                                    {existingPdfUrl && (
                                                        <a
                                                            href={existingPdfUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-primary ms-2"
                                                        >
                                                            <i className="bi bi-eye"></i> View
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="alert alert-warning mt-3">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        Note: PDF files are displayed directly in the mobile app. Users can view but not edit the content.
                                    </div>
                                </div>
                            )}

                            {/* Save Button */}
                            <button
                                className="btn btn-primary mt-4 px-4"
                                onClick={handleSubmit}
                            >
                                <i className="bi bi-save me-2"></i>
                                Save Guidelines
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommunityGuidelines;